import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, Navigate, Link, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useAuth } from '../auth/AuthContext.jsx';
import { peekPostLoginPath } from '../auth/postLoginPath.ts';
import { useAnalytics } from '../hooks/useAnalytics.js';
import { resolveAuthMethod } from '../hooks/useResolveAuthMethod.ts';
import SsoDeprecationBanner from '../components/SsoDeprecationBanner.jsx';
import { ErrorBanner, FieldError } from '../components/ui/index.ts';
import { useServerErrorHandler } from '../hooks/useServerErrorHandler.ts';
import { useSubmitState } from '../hooks/useSubmitState.ts';
import '../styles/pages/auth.css';

/**
 * PasswordInput — uncontrolled show/hide wrapper.
 *
 * After the rhf migration this no longer takes value/onChange. It accepts
 * a `register` prop (the result of calling `form.register('password')`) plus
 * any extra input attributes (id, autoComplete, disabled, aria-*) and spreads
 * them onto the underlying <input>. The toggle behaviour and aria-label are
 * preserved.
 */
function PasswordInput({ id, label, autoComplete, disabled, register, ariaInvalid, ariaDescribedBy }) {
  const [visible, setVisible] = useState(false);

  return (
    <div className="form-field">
      <label htmlFor={id}>{label}</label>
      <div className="password-input-wrapper">
        <input
          id={id}
          type={visible ? 'text' : 'password'}
          autoComplete={autoComplete}
          disabled={disabled}
          aria-invalid={ariaInvalid || undefined}
          aria-describedby={ariaDescribedBy}
          {...(register ?? {})}
        />
        <button
          type="button"
          className="password-toggle"
          onClick={() => setVisible(!visible)}
          aria-label={visible ? 'Hide password' : 'Show password'}
          tabIndex={-1}
        >
          {visible ? 'Hide' : 'Show'}
        </button>
      </div>
    </div>
  );
}

// Cognito returns this specific error when a FORCE_CHANGE_PASSWORD user's
// temporary password has aged past UnusedAccountValidityDays /
// TemporaryPasswordValidityDays. Matching on both name and message keeps us
// from misfiring on garden-variety wrong-password attempts (which would
// trigger unwanted ForgotPassword emails).
const EXPIRED_TEMP_PASSWORD_PATTERN = /temporary password .*expired/i;

function isExpiredInviteError(err) {
  if (!err) return false;
  const name = err.name ?? '';
  const message = err.message ?? '';
  return name === 'NotAuthorizedException' && EXPIRED_TEMP_PASSWORD_PATTERN.test(message);
}

// unified-auth Phase 4: email-first single-page progressive reveal
// (memory-bank/creative/unified-auth-user-journey.md, Option 1 — DECIDED).
// The backend resolve verdict is strictly two-valued (`federate | local` —
// architecture critique C2), so the `local` branch renders BOTH the
// password sign-in AND a non-assertive signup affordance uniformly — never
// disclosing whether an account exists (AC-ERROR-1).
const PHASE = {
  EMAIL_ENTRY: 'EMAIL_ENTRY',
  LOCAL: 'LOCAL',
  FEDERATING: 'FEDERATING',
};

// Best-effort memory of the email a federation attempt was started with, so
// a failed round-trip's "Try again" can restart without asking the user to
// retype — the browser fully navigates away and back during federation, so
// component state alone does not survive the trip.
const PENDING_FEDERATION_EMAIL_KEY = 'banyan_pending_federation_email';

function readPendingFederationEmail() {
  try {
    return sessionStorage.getItem(PENDING_FEDERATION_EMAIL_KEY) || '';
  } catch {
    return '';
  }
}

function writePendingFederationEmail(email) {
  try {
    sessionStorage.setItem(PENDING_FEDERATION_EMAIL_KEY, email);
  } catch {
    /* ignore — private mode / storage unavailable */
  }
}

function clearPendingFederationEmail() {
  try {
    sessionStorage.removeItem(PENDING_FEDERATION_EMAIL_KEY);
  } catch {
    /* ignore */
  }
}

const emailOnlySchema = z.object({
  email: z.string().min(1, 'Email is required').email('Enter a valid email'),
});

const loginSchema = z.object({
  email: z.string().min(1, 'Email is required').email('Enter a valid email'),
  password: z.string().min(1, 'Password is required'),
});

const newPasswordSchema = z
  .object({
    newPassword: z
      .string()
      .min(1, 'Password is required')
      .min(8, 'Password must be at least 8 characters'),
    confirmPassword: z.string().min(1, 'Please confirm your password'),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    path: ['confirmPassword'],
    message: 'Passwords do not match.',
  });

const EMAIL_RESOLVER = zodResolver(emailOnlySchema);
const LOGIN_RESOLVER = zodResolver(loginSchema);
const NEW_PASSWORD_RESOLVER = zodResolver(newPasswordSchema);

export default function LoginPage() {
  const {
    login,
    completeNewPassword,
    forgotPassword,
    signInWithBanyanSSO,
    oauthError,
    quarantineError,
    isAuthenticated,
    isLoading,
  } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { trackEvent } = useAnalytics();

  const sessionExpired = searchParams.get('expired') === '1';
  const isCallbackProcessing = Boolean(
    isLoading && searchParams.get('code') && searchParams.get('state')
  );

  // All hooks MUST be called before any conditional returns (React rules of hooks)
  const emailForm = useForm({
    resolver: EMAIL_RESOLVER,
    defaultValues: { email: '' },
  });
  const {
    register: registerEmail,
    handleSubmit: handleEmailSubmit,
    formState: { errors: emailErrors },
    setValue: setEmailValue,
  } = emailForm;

  const loginForm = useForm({
    resolver: LOGIN_RESOLVER,
    defaultValues: { email: '', password: '' },
  });
  const {
    register: registerLogin,
    handleSubmit: handleLoginSubmit,
    formState: { errors: loginErrors },
    setValue: setLoginValue,
    getValues: getLoginValues,
  } = loginForm;

  const newPasswordForm = useForm({
    resolver: NEW_PASSWORD_RESOLVER,
    defaultValues: { newPassword: '', confirmPassword: '' },
  });
  const {
    register: registerNewPassword,
    handleSubmit: handleNewPasswordSubmit,
    formState: { errors: newPasswordErrors },
  } = newPasswordForm;

  const {
    bannerMessage: loginBanner,
    handleServerError: handleLoginServerError,
    clearBanner: clearLoginBanner,
    setBannerMessage: setLoginBanner,
  } = useServerErrorHandler(loginForm);

  const {
    bannerMessage: newPwBanner,
    handleServerError: handleNewPwServerError,
    clearBanner: clearNewPwBanner,
  } = useServerErrorHandler(newPasswordForm);

  // UAT-R-01: ProtectedRoute clears the stored post-login path as soon as the
  // authenticated visit arrives, which can happen BEFORE login()/completeNewPassword()
  // resolve here. Remember the last non-null destination so later reads
  // (the post-await navigate) don't fall back to "/".
  const destinationRef = useRef(null);
  function resolveDestination() {
    const stored = peekPostLoginPath();
    if (stored) destinationRef.current = stored;
    return destinationRef.current ?? '/';
  }

  const [phase, setPhase] = useState(PHASE.EMAIL_ENTRY);
  const [resolvedEmail, setResolvedEmail] = useState('');
  const [isResolving, setIsResolving] = useState(false);
  const [needsNewPassword, setNeedsNewPassword] = useState(false);
  const [inviteResentTo, setInviteResentTo] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isNewPwSubmitting, setIsNewPwSubmitting] = useState(false);
  const [failedFederationEmail, setFailedFederationEmail] = useState(
    readPendingFederationEmail
  );
  const [federationErrorDismissed, setFederationErrorDismissed] = useState(false);

  // A new federation failure (a fresh oauthError value) should always be
  // visible again, even if a previous one was dismissed earlier in the
  // session.
  useEffect(() => {
    if (oauthError) {
      setFederationErrorDismissed(false);
    }
  }, [oauthError]);

  const resolveSubmitState = useSubmitState({
    isSubmitting: isResolving,
    baseLabel: 'Continue',
    loadingLabel: 'Checking…',
  });
  const submitState = useSubmitState({
    isSubmitting,
    baseLabel: 'Sign in',
    loadingLabel: 'Signing in…',
  });
  const newPwSubmitState = useSubmitState({
    isSubmitting: isNewPwSubmitting,
    baseLabel: 'Set password & sign in',
    loadingLabel: 'Setting password…',
  });

  async function beginFederation(email) {
    setPhase(PHASE.FEDERATING);
    writePendingFederationEmail(email);
    setFailedFederationEmail(email);
    try {
      await signInWithBanyanSSO(email);
      // On success the browser navigates away entirely; nothing after this
      // line runs unless signInWithRedirect itself threw synchronously.
    } catch (err) {
      setPhase(PHASE.EMAIL_ENTRY);
      setLoginBanner(err?.message ?? 'Failed to start sign-in. Please try again.');
    }
  }

  async function attemptResolve(email) {
    setIsResolving(true);
    try {
      const { method } = await resolveAuthMethod(email);
      if (method === 'federate') {
        await beginFederation(email);
      } else {
        // method === 'local': reveal password + signup uniformly, never
        // disclosing whether an account exists (AC-ERROR-1).
        setResolvedEmail(email);
        setLoginValue('email', email);
        setLoginValue('password', '');
        setPhase(PHASE.LOCAL);
      }
    } catch {
      // Availability NFR: a resolve hiccup never hard-blocks all logins —
      // fall back to the password/signup step uniformly, with a retry
      // banner (AC-ERROR handling, resolve-unavailable).
      setResolvedEmail(email);
      setLoginValue('email', email);
      setLoginValue('password', '');
      setPhase(PHASE.LOCAL);
      setLoginBanner(
        "We couldn't check your sign-in options. Enter your password below, or try again."
      );
    } finally {
      setIsResolving(false);
    }
  }

  async function onEmailSubmit(values) {
    clearLoginBanner();
    const email = values.email.trim().toLowerCase();
    await attemptResolve(email);
  }

  function handleChangeEmail() {
    clearLoginBanner();
    setEmailValue('email', resolvedEmail);
    setPhase(PHASE.EMAIL_ENTRY);
  }

  function handleFederationRetry() {
    setFederationErrorDismissed(true);
    if (failedFederationEmail) {
      beginFederation(failedFederationEmail);
    } else {
      setPhase(PHASE.EMAIL_ENTRY);
    }
  }

  function handleUseDifferentEmail() {
    clearPendingFederationEmail();
    setFailedFederationEmail('');
    setFederationErrorDismissed(true);
    setEmailValue('email', '');
    setPhase(PHASE.EMAIL_ENTRY);
  }

  // Redirect to main app if already signed in — back to the page they were
  // heading to before login (AC-ERROR-7; same-origin relative paths only),
  // otherwise "/" as always. This is also where the SSO round trip returns.
  if (!isLoading && isAuthenticated) {
    return <Navigate to={resolveDestination()} replace />;
  }

  // AC-ASYNC-1: the federation callback round-trip must always show an
  // explicit "Signing you in…" state, never a blank/hanging shell — including
  // on a hard refresh mid-callback (the `?code&state` query params are the
  // durable signal AuthContext uses to know a callback is in flight).
  if (isCallbackProcessing) {
    return (
      <div className="auth-page">
        <div className="auth-card" data-testid="callback-processing-view">
          <p className="auth-title" aria-live="polite" aria-busy="true">
            Signing you in…
          </p>
        </div>
      </div>
    );
  }

  if (phase === PHASE.FEDERATING) {
    return (
      <div className="auth-page">
        <div className="auth-card" data-testid="federating-view">
          <p className="auth-title" aria-live="polite" aria-busy="true">
            Redirecting…
          </p>
        </div>
      </div>
    );
  }

  async function onLoginSubmit(values) {
    clearLoginBanner();
    setIsSubmitting(true);
    resolveDestination(); // capture before awaiting login()
    try {
      const result = await login(values.email, values.password);
      if (result?.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
        setNeedsNewPassword(true);
      } else {
        trackEvent('login');
        navigate(resolveDestination(), { replace: true });
      }
    } catch (err) {
      if (isExpiredInviteError(err)) {
        try {
          const response = await forgotPassword(values.email);
          if (response?.method === 'invite') {
            setInviteResentTo(values.email.trim());
            return;
          }
        } catch {
          // Fall through to the original auth error.
        }
      }
      handleLoginServerError(err);
    } finally {
      setIsSubmitting(false);
    }
  }

  async function onNewPasswordSubmit(values) {
    clearNewPwBanner();
    setIsNewPwSubmitting(true);
    resolveDestination(); // capture before awaiting completeNewPassword()
    try {
      await completeNewPassword(values.newPassword);
      navigate(resolveDestination(), { replace: true });
    } catch (err) {
      handleNewPwServerError(err);
    } finally {
      setIsNewPwSubmitting(false);
    }
  }

  if (inviteResentTo) {
    return (
      <div className="auth-page">
        <div className="auth-card">
          <h1 className="auth-title">Invitation resent</h1>
          <p className="auth-subtitle">
            Your previous invite had expired. We've sent a fresh invitation email to{' '}
            <strong>{inviteResentTo}</strong>. Check your inbox and follow the link to set your password.
          </p>
          <p className="auth-link">
            <button
              type="button"
              onClick={() => {
                setInviteResentTo('');
                // Clear password but preserve the email for parity with prior UX.
                setLoginValue('password', '');
              }}
              style={{
                background: 'none',
                border: 'none',
                padding: 0,
                color: 'var(--color-primary)',
                font: 'inherit',
                textDecoration: 'underline',
                cursor: 'pointer',
              }}
            >
              Back to sign in
            </button>
          </p>
        </div>
      </div>
    );
  }

  if (needsNewPassword) {
    return (
      <div className="auth-page">
        <div className="auth-card">
          <h1 className="auth-title">Set a new password</h1>
          <p style={{ color: 'var(--color-text-secondary)', marginBottom: '1.5rem', fontSize: '0.9375rem' }}>
            Your account requires a new password. Please choose a strong password to continue.
          </p>

          <form
            onSubmit={handleNewPasswordSubmit(onNewPasswordSubmit)}
            data-testid="new-password-form"
            noValidate
          >
            {newPwBanner && (
              <ErrorBanner message={newPwBanner} onDismiss={clearNewPwBanner} />
            )}

            <PasswordInput
              id="new-password"
              label="New password"
              autoComplete="new-password"
              disabled={isNewPwSubmitting}
              register={registerNewPassword('newPassword')}
              ariaInvalid={!!newPasswordErrors.newPassword}
              ariaDescribedBy={newPasswordErrors.newPassword ? 'new-password-error' : undefined}
            />
            <FieldError id="new-password-error" error={newPasswordErrors.newPassword} testId="newPassword-error" />

            <PasswordInput
              id="confirm-password"
              label="Confirm password"
              autoComplete="new-password"
              disabled={isNewPwSubmitting}
              register={registerNewPassword('confirmPassword')}
              ariaInvalid={!!newPasswordErrors.confirmPassword}
              ariaDescribedBy={newPasswordErrors.confirmPassword ? 'confirm-password-error' : undefined}
            />
            <FieldError id="confirm-password-error" error={newPasswordErrors.confirmPassword} testId="confirmPassword-error" />

            <button
              type="submit"
              data-testid="submit-btn"
              className="btn-primary btn-full"
              aria-busy={newPwSubmitState.ariaBusy}
              disabled={newPwSubmitState.ariaBusy}
            >
              {newPwSubmitState.buttonLabel}
            </button>
          </form>
        </div>
      </div>
    );
  }

  // AC-ERROR-2: a failed federation round-trip surfaces here (fresh mount
  // after the browser navigated back from a failed IdP redirect) with two
  // recovery actions. Suppressed once the user has acted on it, and
  // re-armed automatically by a new failure (see the effect above).
  const showFederationError =
    phase === PHASE.EMAIL_ENTRY &&
    !!oauthError &&
    !federationErrorDismissed &&
    !quarantineError;

  if (phase === PHASE.LOCAL) {
    return (
      <div className="auth-page">
        <div className="auth-card">
          <h1 className="auth-title">Sign in to Banyan AI</h1>

          {/* sso-warning: pre-sign-in notice; renders only when the env gate is on. */}
          <SsoDeprecationBanner variant="compact" />

          <form
            onSubmit={handleLoginSubmit(onLoginSubmit)}
            data-testid="login-form"
            noValidate
          >
            {loginBanner && (
              <ErrorBanner message={loginBanner} onDismiss={clearLoginBanner} />
            )}

            <div className="form-field" data-testid="locked-email">
              <label htmlFor="email">Email</label>
              <input id="email" type="email" value={resolvedEmail} readOnly disabled />
              <p className="auth-link" style={{ marginTop: '0.25rem' }}>
                <button
                  type="button"
                  data-testid="change-email-btn"
                  onClick={handleChangeEmail}
                  style={{
                    background: 'none',
                    border: 'none',
                    padding: 0,
                    color: 'var(--color-primary)',
                    font: 'inherit',
                    textDecoration: 'underline',
                    cursor: 'pointer',
                  }}
                >
                  Change
                </button>
              </p>
            </div>

            <PasswordInput
              id="password"
              label="Password"
              autoComplete="current-password"
              disabled={isSubmitting}
              register={registerLogin('password')}
              ariaInvalid={!!loginErrors.password}
              ariaDescribedBy={loginErrors.password ? 'password-error' : undefined}
            />
            <FieldError id="password-error" error={loginErrors.password} />

            <p className="auth-link" style={{ textAlign: 'right', marginTop: '-0.25rem', marginBottom: '0.75rem' }}>
              <Link to="/forgot-password" state={{ email: resolvedEmail }}>Forgot password?</Link>
            </p>

            <button
              type="submit"
              data-testid="submit-btn"
              className="btn-primary btn-full"
              aria-busy={submitState.ariaBusy}
              disabled={submitState.ariaBusy}
            >
              {submitState.buttonLabel}
            </button>
          </form>

          {/*
            Non-assertive, existence-safe: this is shown uniformly for every
            `local` verdict — it is a routing hint, never a disclosure that no
            account exists (AC-ERROR-1, AC-HAPPY-4).
          */}
          <p className="auth-link" data-testid="signup-affordance">
            New here?{' '}
            <Link to="/register" state={{ email: resolvedEmail }}>
              Create an account
            </Link>
          </p>
        </div>
      </div>
    );
  }

  // Pre-mount fallback: oauthError is a route-level error from the AuthContext and should
  // surface even before the user submits anything. The login submit error path goes through
  // useServerErrorHandler and lives in `loginBanner`.
  return (
    <div className="auth-page">
      <div className="auth-card">
        <h1 className="auth-title">Sign in to Banyan AI</h1>

        {/*
          C3: a BanyanSSO-federated identity the backend quarantined
          (403 federation_link_quarantined) lands back here after being signed
          out. This is a TERMINAL state — retrying the same federation would
          only re-quarantine — so we show a clear "contact your administrator"
          message rather than the retryable federation-failure banner.
        */}
        {quarantineError && (
          <p className="form-error" role="alert" data-testid="quarantine-error-banner">
            {quarantineError}
          </p>
        )}

        {sessionExpired && (
          <p className="form-error" role="alert">
            Your session has expired. Please sign in again to continue.
          </p>
        )}

        <form
          onSubmit={handleEmailSubmit(onEmailSubmit)}
          data-testid="email-form"
          noValidate
        >
          {showFederationError ? (
            <>
              <ErrorBanner
                message={oauthError}
                action={{ label: 'Try again', onClick: handleFederationRetry }}
                testId="federation-error-banner"
              />
              <p className="auth-link">
                <button
                  type="button"
                  data-testid="use-different-email-btn"
                  onClick={handleUseDifferentEmail}
                  style={{
                    background: 'none',
                    border: 'none',
                    padding: 0,
                    color: 'var(--color-primary)',
                    font: 'inherit',
                    textDecoration: 'underline',
                    cursor: 'pointer',
                  }}
                >
                  Use a different email
                </button>
              </p>
            </>
          ) : (
            loginBanner && <ErrorBanner message={loginBanner} onDismiss={clearLoginBanner} />
          )}

          <div className="form-field">
            <label htmlFor="email">Email</label>
            <input
              id="email"
              type="email"
              autoComplete="email"
              autoFocus
              disabled={isResolving}
              aria-invalid={!!emailErrors.email || undefined}
              aria-describedby={emailErrors.email ? 'email-error' : undefined}
              {...registerEmail('email')}
            />
            <FieldError id="email-error" error={emailErrors.email} />
          </div>

          <button
            type="submit"
            data-testid="submit-btn"
            className="btn-primary btn-full"
            aria-busy={resolveSubmitState.ariaBusy}
            disabled={resolveSubmitState.ariaBusy}
          >
            {resolveSubmitState.buttonLabel}
          </button>
        </form>
      </div>
    </div>
  );
}
